import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Clock, CheckCircle2, XCircle, Phone, Users,
  Upload, Download, AlertTriangle, FileText,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PremiumWorkerForm from "@/components/PremiumWorkerForm";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_PILL = {
  pending:    { label: "Pending review", icon: Clock,        cls: "border-[#F4B400] text-[#F4B400]" },
  approved:   { label: "Approved · LIVE", icon: CheckCircle2, cls: "border-[#34C759] text-[#34C759]" },
  rejected:   { label: "Rejected",        icon: XCircle,      cls: "border-[#FF453A] text-[#FF453A]" },
  needs_call: { label: "Needs call",      icon: Phone,        cls: "border-[#FF9500] text-[#FF9500]" },
};

export default function AgencyDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("upload"); // upload | bulk | roster
  const [submissions, setSubmissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Bulk-upload state
  const fileRef = useRef(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Soft gate — wait for auth bootstrap to finish before redirecting
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/agencies/dashboard");
      return;
    }
    if (user.role !== "agency" && user.role !== "recruiter") {
      toast.error("Agency or recruiter account required.");
      navigate("/agencies/register");
    }
  }, [user, authLoading, navigate]);

  const loadRoster = useCallback(async () => {
    try {
      const r = await api.get("/premium/my-submissions");
      setSubmissions(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load your roster.");
    }
  }, []);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const handleUpload = async (payload) => {
    setSubmitting(true);
    try {
      await api.post("/premium/agency-upload", payload);
      toast.success("Uploaded. Staff review within 24 h.");
      setTab("roster");
      loadRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkFile = async (file) => {
    if (!file) return;
    const ok = /\.(csv|tsv|txt)$/i.test(file.name);
    if (!ok) {
      toast.error("Please upload a .csv file (export your Excel as CSV).");
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/premium/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(r.data);
      toast.success(`${r.data.created_count} workers queued · ${r.data.skipped_count} skipped`);
      loadRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed.");
    } finally {
      setBulkUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleBulkFile(f);
  };

  const counts = submissions.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white" data-testid="agency-dashboard">
      <PublicHeader />

      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">/ AGENCY DASHBOARD</div>
              <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                {user?.company_name || "Your agency"}
              </h1>
              <p className="text-white/55 mt-2 text-sm">{user?.full_name} · {user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              {Object.entries(counts).map(([status, n]) => {
                const cfg = STATUS_PILL[status];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return (
                  <div key={status} className={`border ${cfg.cls} px-3 py-2 flex items-center gap-2`}>
                    <Icon className="w-4 h-4" />
                    <span className="font-mono text-xs font-bold">{n}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest">{cfg.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tabs */}
          <div className="inline-flex gap-1 border border-white/10 p-1 bg-[#141416]">
            <button
              onClick={() => setTab("upload")}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${tab === "upload" ? "bg-white text-black" : "text-white/65 hover:text-white"}`}
              data-testid="ad-tab-upload"
            >
              <Plus className="w-3.5 h-3.5" /> Upload new worker
            </button>
            <button
              onClick={() => setTab("bulk")}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${tab === "bulk" ? "bg-[#FF9500] text-black" : "text-white/65 hover:text-white"}`}
              data-testid="ad-tab-bulk"
            >
              <Upload className="w-3.5 h-3.5" /> Bulk upload (CSV)
            </button>
            <button
              onClick={() => setTab("roster")}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${tab === "roster" ? "bg-white text-black" : "text-white/65 hover:text-white"}`}
              data-testid="ad-tab-roster"
            >
              <Users className="w-3.5 h-3.5" /> My roster · {submissions.length}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {tab === "upload" ? (
          <div className="max-w-3xl">
            <h2 className="font-display font-bold text-2xl mb-2">Upload a worker</h2>
            <p className="text-white/55 mb-8 text-sm">
              Fill out the worker's profile. We review within 24 h. Approved
              workers appear on the Premium list — every employer sees them first.
            </p>
            <PremiumWorkerForm mode="agency" onSubmit={handleUpload} submitting={submitting} />
          </div>
        ) : tab === "bulk" ? (
          <div className="max-w-3xl">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <h2 className="font-display font-bold text-2xl mb-2">Bulk upload from CSV</h2>
                <p className="text-white/55 text-sm max-w-xl">
                  Export your roster from Excel / your ATS as a CSV, drop it
                  below, and we'll create one Premium submission per row.
                  Up to 500 rows or 2&nbsp;MB per upload.
                </p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/premium/bulk-template.csv`}
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white px-4 py-3 text-sm font-bold transition-colors"
                data-testid="ad-bulk-template"
              >
                <Download className="w-4 h-4" /> Download template
              </a>
            </div>

            {/* Drag-drop zone */}
            <label
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={`block border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${
                dragActive ? "border-[#FF9500] bg-[#FF9500]/[0.05]" : "border-white/20 hover:border-white/40 bg-white/[0.02]"
              }`}
              data-testid="ad-bulk-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt,text/csv"
                onChange={(e) => handleBulkFile(e.target.files?.[0])}
                className="sr-only"
                data-testid="ad-bulk-file-input"
              />
              <Upload className="w-10 h-10 mx-auto mb-4 text-white/60" strokeWidth={1.5} />
              {bulkUploading ? (
                <div className="font-display font-bold text-lg">Uploading and parsing rows…</div>
              ) : (
                <>
                  <div className="font-display font-bold text-lg mb-1">Drop your CSV here</div>
                  <div className="text-sm text-white/55">or click to choose a file</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-4">
                    Required columns · full_name · email · country · role · headline · experience_years
                  </div>
                </>
              )}
            </label>

            {/* Result panel */}
            {bulkResult && (
              <div className="mt-8 space-y-4" data-testid="ad-bulk-result">
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-white/10 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Processed</div>
                    <div className="font-display font-black text-3xl">{bulkResult.rows_processed}</div>
                  </div>
                  <div className="border border-[#34C759]/40 bg-[#34C759]/[0.05] p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#34C759]">Queued</div>
                    <div className="font-display font-black text-3xl text-[#34C759]">{bulkResult.created_count}</div>
                  </div>
                  <div className="border border-[#FF9500]/40 bg-[#FF9500]/[0.05] p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500]">Skipped</div>
                    <div className="font-display font-black text-3xl text-[#FF9500]">{bulkResult.skipped_count}</div>
                  </div>
                </div>

                {bulkResult.created?.length > 0 && (
                  <div className="border border-[#34C759]/30 p-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#34C759] mb-3">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Successfully queued
                    </div>
                    <ul className="space-y-1 text-sm">
                      {bulkResult.created.slice(0, 30).map((c) => (
                        <li key={c.id} className="flex items-center gap-3">
                          <FileText className="w-3 h-3 text-white/40" />
                          <span className="font-bold">{c.name}</span>
                          <span className="text-white/40 font-mono text-xs">{c.email}</span>
                          <span className="ml-auto text-white/40 font-mono text-[10px]">row {c.row}</span>
                        </li>
                      ))}
                      {bulkResult.created.length > 30 && (
                        <li className="text-white/50 text-xs">…and {bulkResult.created.length - 30} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {bulkResult.errors?.length > 0 && (
                  <div className="border border-[#FF9500]/30 p-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FF9500] mb-3">
                      <AlertTriangle className="w-3.5 h-3.5" /> Rows skipped — fix and re-upload only these
                    </div>
                    <ul className="space-y-2 text-sm">
                      {bulkResult.errors.map((er, i) => (
                        <li key={i} className="flex items-start gap-3 border-b border-white/5 pb-2 last:border-0">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1 w-12">row&nbsp;{er.row}</span>
                          <div className="flex-1">
                            <div>
                              {er.name && <span className="font-bold">{er.name} </span>}
                              {er.email && <span className="text-white/50 font-mono text-xs">{er.email}</span>}
                            </div>
                            <div className="text-[#FF9500] text-xs mt-0.5">{er.reason}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <h2 className="font-display font-bold text-2xl mb-6">Your roster</h2>
            {submissions.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center text-white/55">
                No uploads yet. <button onClick={() => setTab("upload")} className="text-white underline">Upload your first worker →</button>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map((s) => {
                  const cfg = STATUS_PILL[s.status] || STATUS_PILL.pending;
                  const Icon = cfg.icon;
                  return (
                    <div key={s.id} className="border border-white/10 p-5 hover:border-white/30 transition-colors flex items-start justify-between gap-4 flex-wrap" data-testid={`roster-row-${s.id}`}>
                      <div>
                        <div className="font-display font-bold text-lg">{s.full_name}</div>
                        <div className="text-sm text-white/70 mt-0.5">{s.headline}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/45 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          <span>{s.role}</span>
                          <span>{s.country}{s.city ? ` · ${s.city}` : ""}</span>
                          <span>{s.experience_years} yrs</span>
                          <span>{new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className={`border ${cfg.cls} px-3 py-2 flex items-center gap-2 self-start`}>
                        <Icon className="w-4 h-4" />
                        <span className="font-mono text-[10px] uppercase tracking-widest">{cfg.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
